// src/features/document/documentSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ApiError, CreateDocumentPayload, DocumentResponse, DocumentState, UpdateDocumentActionPayload, UpdateDocumentPayload } from './jobTypes';
import { createDocumentTemplate, fetchDocumentTemplate, fetchJobPostReportData, fetchJobsDataList, updateDocumentTags, updateDocumentTemplate } from '../../services/jobServices';

export const createJob = createAsyncThunk<
  DocumentResponse,
  CreateDocumentPayload,
  { rejectValue: string }
>(
  'document/createJob',
  async (payload, { rejectWithValue }) => {
    try {
      if (!payload.job_title) {
        return rejectWithValue('Name is required');
      }
      if (!payload.baseId || isNaN(payload.baseId)) {
        return rejectWithValue('Valid base ID is required');
      }

      const response = await createDocumentTemplate(payload);
      return response;
    } catch (err) {
      const error = err as ApiError;
      return rejectWithValue(error.response?.data?.message || 'Failed to create document');
    }
  }
);

// Update Document Thunk
export const updateDocument = createAsyncThunk<
  DocumentResponse,
  UpdateDocumentPayload,
  { rejectValue: string }
>(
  'document/updateDocument',
  async (payload, { rejectWithValue }) => {
    try {
      // if (!payload.name) {
      //   return rejectWithValue('Name is required');
      // }
      if (!payload.baseId || isNaN(payload.baseId)) {
        return rejectWithValue('Valid base ID is required');
      }

      const response = await updateDocumentTemplate(payload);
      return response;
    } catch (err) {
      const error = err as ApiError;
      return rejectWithValue(error.response?.data?.message || 'Failed to update document');
    }
  }
);

//fetch document template 
export const fetchDocument = createAsyncThunk<
  DocumentResponse,
  { baseId: string; id: string },
  { rejectValue: string }
>(
  'document/fetchDocument',
  async ({ baseId, id }, { rejectWithValue }) => {
    try {
      const response = await fetchDocumentTemplate(baseId, id);
      return response;
    } catch (err) {
      const error = err as ApiError;
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch document');
    }
  }
);

// job list

export const fetchJobList = createAsyncThunk<
  DocumentResponse,
  { baseId: number },
  { rejectValue: string }
>(
  'document/fetchJobList',
  async ({ baseId }, { rejectWithValue }) => {
    try {
      const response = await fetchJobsDataList(baseId);
      return response;
    } catch (err) {
      const error = err as ApiError;
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch document');
    }
  }
);

export const updateTags = createAsyncThunk<
  DocumentResponse,
  UpdateDocumentActionPayload,
  { rejectValue: string }
>(
  'document/updateTags',
  async (payload, { rejectWithValue }) => {
    try {
      if (!payload.baseId) {
        return rejectWithValue('Valid base ID is required');
      }

      const response = await updateDocumentTags(payload);
      return response;
    } catch (err) {
      const error = err as ApiError;
      return rejectWithValue(error.response?.data?.message || 'Failed to update document');
    }
  }
);


//fetch document template 
export const fetchJobReport = createAsyncThunk<
  DocumentResponse,
  { baseId: string; jobId: string; fromDate?: string; toDate?: string },
  { rejectValue: string }
>(
  'document/fetchJobReport',
  async ({ baseId, jobId, fromDate, toDate }, { rejectWithValue }) => {
    try {
      const response = await fetchJobPostReportData(baseId, jobId, fromDate, toDate);
      return response;
    } catch (err) {
      const error = err as ApiError;
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch document');
    }
  }
);





const initialState: DocumentState = {
  data: null,
  createStatus: 'idle',
  createError: null,
  updateStatus: 'idle',
  updateError: null,
  fetchStatus: 'idle',
  fetchError: null,
  createContactStatus: 'idle',
  createContactError: null,
  fetchContactStatus: 'idle',
  fetchContactError: null,
  updateContactStatus: 'idle',
  updateContactError: null,
  createDocumentActionStatus: 'idle',
  createDocumentActionError: null,
  updateDocumentActionStatus: 'idle',
  updateDocumentActionError: null,
  fetchDocumentActionStatus: 'idle',
  fetchDocumentActionError: null,
  fetchJobListStatus: 'idle',
  fetchJobListError: null,
  updateItemMemberStatus: 'idle',
  updateItemMemberError: null,
  // report
  fetchReportStatus: 'idle',
  fetchReportError: null,
  fetchReportData: null

}


const jobSlices = createSlice({
  name: 'job',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Cases for createDocument
      .addCase(createJob.pending, (state) => {
        state.createStatus = 'loading';
        state.createError = null; // Clear previous errors
      })
      .addCase(createJob.fulfilled, (state, action: PayloadAction<DocumentResponse>) => {
        state.createStatus = 'succeeded';
        state.data = action.payload;
      })
      .addCase(createJob.rejected, (state, action) => {
        state.createStatus = 'failed';
        state.createError = action.payload || 'Failed to create document';
      })
      // Cases for updateDocument
      .addCase(updateDocument.pending, (state) => {
        state.updateStatus = 'loading';
        state.updateError = null; // Clear previous errors
      })
      .addCase(updateDocument.fulfilled, (state, action: PayloadAction<DocumentResponse>) => {
        state.updateStatus = 'succeeded';
        state.data = action.payload;
      })
      .addCase(updateDocument.rejected, (state, action) => {
        state.updateStatus = 'failed';
        state.updateError = action.payload || 'Failed to update document';
      })
      // Cases for fetchDocument
      .addCase(fetchDocument.pending, (state) => {
        state.fetchStatus = 'loading';
        state.fetchError = null; // Clear previous errors
      })
      .addCase(fetchDocument.fulfilled, (state, action: PayloadAction<DocumentResponse>) => {
        state.fetchStatus = 'succeeded';
        state.data = action.payload;
      })
      .addCase(fetchDocument.rejected, (state, action) => {
        state.fetchStatus = 'failed';
        state.fetchError = action.payload || 'Failed to fetch document';
      })
      // Cases for fetchJobList
      .addCase(fetchJobList.pending, (state) => {
        state.fetchJobListStatus = 'loading';
        state.fetchJobListError = null; // Clear previous errors
      })
      .addCase(fetchJobList.fulfilled, (state, action: PayloadAction<DocumentResponse>) => {
        state.fetchJobListStatus = 'succeeded';
        state.data = action.payload;
      })
      .addCase(fetchJobList.rejected, (state, action) => {
        state.fetchJobListStatus = 'failed';
        state.fetchJobListError = action.payload || 'Failed to fetch document';
      })
      // Cases for updateTags
      .addCase(updateTags.pending, (state) => {
        state.updateDocumentActionStatus = 'loading';
        state.updateDocumentActionError = null; // Clear previous errors
      })
      .addCase(updateTags.fulfilled, (state, action: PayloadAction<DocumentResponse>) => {
        state.updateDocumentActionStatus = 'succeeded';
        state.data = action.payload;
      })
      .addCase(updateTags.rejected, (state, action) => {
        state.updateDocumentActionStatus = 'failed';
        state.updateDocumentActionError = action.payload || 'Failed to update document';
      })
      // Cases for fetchJobReport
      .addCase(fetchJobReport.pending, (state) => {
        state.fetchReportStatus = 'loading';
        state.fetchReportError = null; // Clear previous errors
      })
      .addCase(fetchJobReport.fulfilled, (state, action: PayloadAction<DocumentResponse>) => {
        state.fetchReportStatus = 'succeeded';
        state.fetchReportData = action.payload;
      })
      .addCase(fetchJobReport.rejected, (state, action) => {
        state.fetchReportStatus = 'failed';
        state.fetchReportError = action.payload || 'Failed to fetch document';
      });
  }
});

export default jobSlices.reducer;
